import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMe, useInvalidateMe } from '@/hooks/useMe';
import { depositsApi, kycApi } from '@/api/endpoints';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCents, parseDollarsToCents } from '@/utils/money';
import { PayIdDepositPanel } from './payid/PayIdDepositPanel';

const LOCK_TERMS = [
  { months: 3,  label: '3 months',  desc: 'Shortest commitment' },
  { months: 6,  label: '6 months',  desc: 'Most popular' },
  { months: 9,  label: '9 months',  desc: '' },
  { months: 12, label: '12 months', desc: 'Unlock all at once' },
] as const;

type LockTerm = 3 | 6 | 9 | 12;

const QUICK_AMOUNTS = [20, 50, 100, 200] as const;

/** How long to poll for async payment confirmation after a return redirect. */
const RETURN_POLL_INTERVAL_MS = 2_000;
const RETURN_POLL_MAX_ATTEMPTS = 20; // 40 s total; the webhook usually lands < 10 s

// KYC settles ASYNCHRONOUSLY under a real provider: we create the check, the
// customer completes it in an embedded frame or a new tab, and the provider
// webhooks the outcome back to us minutes later. Nothing in this tab knows when
// that lands, so a 'pending' status must be polled — without this the customer
// finishes verifying and still sees "in progress" until they manually reload,
// which reads as a broken deposit flow at the exact moment they were ready to
// pay. Provider-agnostic on purpose: it re-reads /me and asks no provider
// anything.
const KYC_POLL_INTERVAL_MS = 3_000;
/** Syntrico requires E.164; the server enforces the same pattern (0052). */
const E164 = /^\+[1-9][0-9]{7,14}$/;
const KYC_POLL_MAX_ATTEMPTS = 60; // 3 min, then we stop and tell them plainly

// Which deposit rail the SPA offers. 'payid' swaps the amount-first mock form
// for the Zepto inbound PayID panel. Build-time for now so production is
// byte-identical until the api contract lands; it then moves to a /me param
// so the operator can flip it without a web deploy.
const DEPOSIT_RAIL = (import.meta.env.VITE_DEPOSIT_RAIL as string | undefined) === 'payid' ? 'payid' : 'mock';

export function DepositPage() {
  const { data: me } = useMe();
  const invalidateMe = useInvalidateMe();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const paymentReturn = searchParams.get('stripe_return') === '1';
  const paymentCancel = searchParams.get('stripe_cancel') === '1';

  // Async-return confirmation poll: after a hosted payment page redirects back
  // we don't yet know if the provider webhook has settled the deposit. Poll
  // /me until cumulative deposits change, then clean the URL.
  const [confirming, setConfirming] = useState(paymentReturn);
  const [confirmFailed, setConfirmFailed] = useState(false);
  const pollAttemptsRef = useRef(0);
  const baselineDepositsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!paymentReturn || !confirming) return;
    if (baselineDepositsRef.current === null && me) {
      baselineDepositsRef.current = me.balances.cumulativeDepositsCents;
    }
    const timer = setInterval(async () => {
      pollAttemptsRef.current += 1;
      await invalidateMe();
      const current = me?.balances.cumulativeDepositsCents ?? 0;
      const baseline = baselineDepositsRef.current ?? 0;
      if (current > baseline) {
        clearInterval(timer);
        setConfirming(false);
        navigate('/deposit', { replace: true });
        return;
      }
      if (pollAttemptsRef.current >= RETURN_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
        setConfirming(false);
        setConfirmFailed(true);
      }
    }, RETURN_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentReturn, confirming, me?.balances.cumulativeDepositsCents]);

  const [kycPollExhausted, setKycPollExhausted] = useState(false);
  const kycPollAttemptsRef = useRef(0);

  const [dollars, setDollars]       = useState('');
  const [lockTerm, setLockTerm]     = useState<LockTerm>(6);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');
  const [kycError, setKycError]     = useState('');
  // 0052: Syntrico creates the client from name + E.164 mobile. Both are
  // collected here (never at signup) only while /me has no stored value.
  const [kycLastName, setKycLastName] = useState('');
  const [kycPhone, setKycPhone]       = useState('');
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);

  const amountCents     = parseDollarsToCents(dollars || '0');
  const minDepositCents = me?.params?.minDepositCents ?? 100;
  const isFirst         = (me?.balances.cumulativeDepositsCents ?? 0) === 0;
  const feeBps          = me?.params?.transactionFeeBps ?? 400;
  const kycStatus       = me?.user.kycStatus ?? 'unverified';
  const storedLastName  = me?.user.lastName ?? null;
  const storedPhone     = me?.user.phone ?? null;
  const needsLastName   = !storedLastName;
  const needsPhone      = !storedPhone;
  const phoneOk         = !needsPhone || E164.test(kycPhone.trim());
  const lastNameOk      = !needsLastName || kycLastName.trim().length > 0;
  const verified        = kycStatus === 'verified';

  // Poll while the check is outstanding. Re-reading /me is the whole mechanism —
  // the webhook has already flipped users.kyc_status by the time we see it, so
  // there is nothing provider-specific here. Stops as soon as the status leaves
  // 'pending', and gives up after KYC_POLL_MAX_ATTEMPTS rather than spinning
  // forever against a check that will never land.
  useEffect(() => {
    if (kycStatus !== 'pending' || kycPollExhausted) return;
    const timer = setInterval(async () => {
      kycPollAttemptsRef.current += 1;
      await invalidateMe();
      if (kycPollAttemptsRef.current >= KYC_POLL_MAX_ATTEMPTS) {
        clearInterval(timer);
        setKycPollExhausted(true);
      }
    }, KYC_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus, kycPollExhausted]);

  const savingsBalance  = me?.balances.savingsCents ?? 0;
  const playBalance     = me?.balances.balanceCents ?? 0;

  // v0.11 — first-deposit split: how much locks into Savings vs immediately
  // playable Play Balance. Anchored at the admin-recommended default until the
  // user drags the slider.
  const recommendedSplitBps = me?.params?.recommendedSavingsSplitBps ?? 7500;
  const [savingsPct, setSavingsPct] = useState(recommendedSplitBps / 100);
  const savingsTouchedRef = useRef(false);
  useEffect(() => {
    if (!savingsTouchedRef.current) setSavingsPct(recommendedSplitBps / 100);
  }, [recommendedSplitBps]);

  const savingsCents     = isFirst ? Math.round(amountCents * (savingsPct / 100)) : 0;
  const playBalanceCents = amountCents - savingsCents;

  const lockUntilPreview = (() => {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() + lockTerm);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  })();

  // --- KYC (pre-deposit) ---
  const verify = useMutation({
    mutationFn: () =>
      kycApi.initiate({
        documentType: 'driver_licence',
        ...(needsLastName ? { lastName: kycLastName.trim() } : {}),
        ...(needsPhone ? { phone: kycPhone.trim() } : {}),
      }),
    // A fresh check is a fresh poll window — reset here rather than in the poll
    // effect, because starting a new check is the ONLY way back into 'pending'
    // and resetting inside the effect is a cascading render.
    onMutate: () => { kycPollAttemptsRef.current = 0; setKycPollExhausted(false); },
    onSuccess: async (res) => {
      await invalidateMe();
      setVerificationUrl(res.verificationUrl ?? null);
      if (res.kycStatus === 'rejected') {
        setKycError(res.rejectReason || 'Verification could not be completed. Contact support.');
      }
    },
    onError: (err: Error) => setKycError(err.message || 'Verification failed — please try again.'),
  });

  // --- Deposit ---
  const deposit = useMutation({
    mutationFn: () =>
      depositsApi.create({
        amountCents,
        lockTermMonths: lockTerm,
        paymentMethodToken: 'mock_token', // async provider flow replaces this
        ...(isFirst ? { savingsCents } : {}),
      }),
    // NO hosted-redirect branch.
    //
    // `Deposit.redirectUrl` was the Stripe Checkout hand-off and it is REMOVED
    // from the contract by the Stripe-rail removal, so reading it stops
    // compiling the moment `npm run generate:types` runs — here, in `web/`,
    // which the api-side gates do not cover. The branch was already
    // unreachable: the live rail is `mock`, which settles POST /deposits
    // synchronously and returns no URL, and Zepto's PayTo/Direct Debit flow
    // declares no redirect field at all.
    //
    // It is NOT replaced with a guess. When an async rail needs a hand-off,
    // the contract gains the field first and this branch comes back derived
    // from it — inventing a field name here is how the SPA and the server
    // disagree about what a deposit returns.
    onSuccess: (data) => {
      invalidateMe();
      setSuccessMsg(
        isFirst
          ? `Deposited ${formatCents(data.amountCents)} — ${formatCents(savingsCents)} locked in Savings ` +
            `(until ${lockUntilPreview}), ${formatCents(playBalanceCents)} ready to play.`
          : `Deposited ${formatCents(data.amountCents)} to your Play Balance.`,
      );
      setDollars('');
    },
    onError: (err: Error) => setErrorMsg(err.message || 'Deposit failed. Please try again.'),
  });

  const belowMin  = amountCents > 0 && amountCents < minDepositCents;
  const canSubmit = verified && amountCents >= minDepositCents && !deposit.isPending;

  // Async-return confirming overlay.
  if (confirming) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin" />
        <p className="text-white/70 text-sm">Confirming your payment…</p>
        <p className="text-white/30 text-xs">This usually takes a few seconds.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {/* Header */}
      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-teal">Add funds</span>
        <h1 className="font-display font-extrabold text-3xl sm:text-[34px] tracking-[-0.02em] mt-1">
          Add funds <em className="not-italic text-brand-green">and start.</em>
        </h1>
        <p className="text-white/50 text-sm mt-2 max-w-md">
          {isFirst
            ? 'Your first deposit splits into locked Savings and a Play Balance you can bet with right away.'
            : 'Top-ups go straight to your Play Balance — bet with it or withdraw it any time.'}
        </p>
      </div>

      {/* Current balances context */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-brand-green/20 bg-gradient-to-br from-[#0e2e1c] to-[#082014] p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-green/80">Savings (locked)</div>
          <div className="mt-1 font-display text-2xl font-extrabold tabular-nums">{formatCents(savingsBalance)}</div>
        </div>
        <div className="rounded-2xl border border-brand-teal/20 bg-gradient-to-br from-[#102f3d] to-[#0a1c2c] p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-teal/90">Play Balance</div>
          <div className="mt-1 font-display text-2xl font-extrabold tabular-nums">{formatCents(playBalance)}</div>
        </div>
      </div>

      {/* Payment cancel / confirm-timeout banners */}
      {paymentCancel && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-400">
          Payment cancelled — no money was taken. You can try again below.
        </div>
      )}
      {confirmFailed && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-400">
          We couldn't confirm your payment automatically. If money was taken, contact support —
          your deposit will be credited manually once we verify it. Otherwise, try again below.
        </div>
      )}

      {/* KYC gate — required before the first deposit (AU AML/CTF) */}
      {!verified && (
        <Card className="border-brand-teal/25">
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-xl grid place-items-center bg-brand-teal/10 text-brand-teal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 4v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V7l7-4z" /><path d="m9 12 2 2 4-4" /></svg>
            </span>
            <div className="flex-1">
              <div className="font-display font-bold text-[15px]">Verify your identity first</div>
              <p className="text-sm text-white/55 mt-1">
                Australian law (AML/CTF) requires us to verify your ID before your first deposit. It only takes a moment.
              </p>
              {kycStatus === 'pending' ? (
                verificationUrl ? (
                  <div className="mt-3">
                    <p className="text-sm text-amber-400/90">Verification started. Finish it with our verification partner, then come back here.</p>
                    <a
                      href={verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 rounded-xl bg-brand-teal px-4 py-2 text-sm font-semibold text-[#052e35] hover:brightness-105 transition"
                    >
                      Continue verification
                    </a>
                  </div>
                ) : kycPollExhausted ? (
                  <p className="text-sm text-amber-400/90 mt-3">
                    Your verification is still being reviewed. It can take a little longer when a
                    check needs a manual look. Refresh this page to check again, or contact support
                    if it hasn't cleared within a few hours — nothing has been charged.
                  </p>
                ) : (
                  <p className="text-sm text-amber-400/90 mt-3 flex items-center gap-2">
                    <span className="shrink-0 w-3.5 h-3.5 border-2 border-amber-400/80 border-t-transparent rounded-full animate-spin" />
                    Verification started — we'll unlock deposits as soon as it clears.
                  </p>
                )
              ) : (
                <div className="mt-3">
                  {/* Identity fields appear only while /me has no stored value; the
                      server stores them on first use and later calls send none.
                      Nothing here is sent to the provider until the button. */}
                  {(needsLastName || needsPhone) && (
                    <div className="grid gap-3 sm:grid-cols-2 mb-3">
                      {needsLastName && (
                        <Input
                          label="Last name"
                          autoComplete="family-name"
                          placeholder="As on your ID"
                          value={kycLastName}
                          onChange={(e) => { setKycLastName(e.target.value); setKycError(''); }}
                        />
                      )}
                      {needsPhone && (
                        <Input
                          label="Mobile"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="+61 4xx xxx xxx"
                          value={kycPhone}
                          onChange={(e) => { setKycPhone(e.target.value.replace(/[\s()-]/g, '')); setKycError(''); }}
                          hint={kycPhone && !phoneOk ? 'Enter your mobile in international form, e.g. +61412345678.' : undefined}
                        />
                      )}
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    loading={verify.isPending}
                    disabled={!phoneOk || !lastNameOk}
                    onClick={() => { setKycError(''); verify.mutate(); }}
                  >
                    {verify.isPending ? 'Starting…' : 'Verify my identity'}
                  </Button>
                </div>
              )}
              {kycError && <p className="text-sm text-red-400 mt-2">{kycError}</p>}
            </div>
          </div>
        </Card>
      )}

      {DEPOSIT_RAIL === 'payid' ? (
        <PayIdDepositPanel />
      ) : (<>
      {/* Deposit form */}
      <Card title="Make a deposit" className={!verified ? 'opacity-60 pointer-events-none select-none' : ''}>
        {/* Amount */}
        <div className="mb-5">
          <label className="block text-sm text-white/60 mb-1.5">Amount (AUD)</label>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder={`Min ${formatCents(minDepositCents)}`}
            value={dollars}
            onChange={(e) => { setDollars(e.target.value); setSuccessMsg(''); setErrorMsg(''); }}
          />
          <div className="flex gap-2 mt-2.5">
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => { setDollars(String(amt)); setSuccessMsg(''); setErrorMsg(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition ${
                  dollars === String(amt)
                    ? 'border-brand-teal bg-brand-teal/10 text-brand-teal'
                    : 'border-white/10 bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white'
                }`}
              >
                ${amt}
              </button>
            ))}
          </div>
          {belowMin && (
            <p className="text-xs text-red-400 mt-2">Minimum deposit is {formatCents(minDepositCents)}.</p>
          )}
        </div>

        {/* Lock term */}
        <div className="mb-5">
          <label className="block text-sm text-white/60 mb-2">Lock term</label>
          <div className="grid grid-cols-2 gap-2">
            {LOCK_TERMS.map(({ months, label, desc }) => (
              <button
                key={months}
                type="button"
                onClick={() => setLockTerm(months)}
                className={`py-2.5 px-3 rounded-xl text-left transition border ${
                  lockTerm === months
                    ? 'border-brand-teal bg-brand-teal/10 text-white'
                    : 'border-white/10 bg-white/[0.04] text-white/60 hover:bg-white/[0.08]'
                }`}
              >
                <div className="font-medium text-sm">{label}</div>
                {desc && <div className="text-xs text-white/40 mt-0.5">{desc}</div>}
              </button>
            ))}
          </div>
          {amountCents >= minDepositCents && (
            <p className="text-xs text-white/40 mt-2">
              Unlocks into your withdrawable Play Balance on <strong className="text-white/60">{lockUntilPreview}</strong>{' '}
              (or earlier if you win a bet).
            </p>
          )}
        </div>

        {/* First-deposit split */}
        {isFirst && amountCents >= minDepositCents && (
          <div className="mb-5 p-4 rounded-2xl bg-white/[0.03] border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-white/80 font-medium">Split your first deposit</label>
              <span className="text-xs text-white/45">{savingsPct}% to Savings</span>
            </div>

            {/* Proportional split bar */}
            <div className="flex h-2.5 rounded-full overflow-hidden bg-white/5 mb-3">
              <div className="bg-brand-green/70 transition-all" style={{ width: `${savingsPct}%` }} />
              <div className="bg-brand-teal/70 transition-all" style={{ width: `${100 - savingsPct}%` }} />
            </div>

            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={savingsPct}
              onChange={(e) => { savingsTouchedRef.current = true; setSavingsPct(Number(e.target.value)); }}
              className="w-full accent-brand-teal"
            />
            <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
              <div className="rounded-xl bg-brand-green/[0.07] border border-brand-green/25 px-3 py-2">
                <div className="text-xs text-brand-green/80">Locked Savings</div>
                <div className="font-semibold text-white tabular-nums">{formatCents(savingsCents)}</div>
              </div>
              <div className="rounded-xl bg-brand-teal/10 border border-brand-teal/30 px-3 py-2">
                <div className="text-xs text-brand-teal/85">Play Balance</div>
                <div className="font-semibold text-white tabular-nums">{formatCents(playBalanceCents)}</div>
              </div>
            </div>
            <p className="text-xs text-white/40 mt-2">
              We recommend keeping most of it locked — that's the money you get back on {lockUntilPreview}, win or lose.
              The Play Balance is yours to bet with right now.
            </p>
          </div>
        )}

        {/* Fee note */}
        <p className="text-xs text-white/30 mb-5">
          A {feeBps / 100}% fee applies on each bet placed from your Play Balance.
        </p>

        {/* CTA */}
        <Button onClick={() => deposit.mutate()} disabled={!canSubmit} loading={deposit.isPending} className="w-full">
          {deposit.isPending
            ? 'Processing…'
            : amountCents >= minDepositCents
              ? `Add funds (${formatCents(amountCents)})`
              : 'Enter an amount'}
        </Button>

        {successMsg && <p className="mt-3 text-sm text-brand-teal text-center">{successMsg}</p>}
        {errorMsg && <p className="mt-3 text-sm text-red-400 text-center">{errorMsg}</p>}
      </Card>
      </>)}

      {/* How it works */}
      <Card title="How deposits work">
        <div className="flex flex-col gap-3 text-sm text-white/60">
          <p>On your first deposit you choose the split: part locks into <strong className="text-white">Savings</strong>,
            the rest lands in your <strong className="text-white">Play Balance</strong> to bet with straight away.
            Later deposits go entirely to Play Balance.</p>
          <p>Locked Savings stays put until the term ends — then it unlocks into your withdrawable
            Play Balance automatically, no bet required.</p>
          <p>Win a bet? Savings unlocks faster. Lose a bet? Your stake goes back
            into Savings (forced saving). Either way, your money doesn't disappear.</p>
          <p>Need out early? Contact support — a compliance team member can unlock on hardship grounds.</p>
        </div>
      </Card>
    </div>
  );
}
